import { styled } from '@mui/material/styles'
import { Badge, Box, Card, Collapse, IconButton, Typography } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import { FC, ReactNode, useState } from 'react'
import { useListContext, useTranslate } from 'react-admin'
import { border } from '../../layout/theme/border'
import { themeColor } from '../../layout/theme/color'
import { cardSurfaceSx } from './styles'

const PREFIX = 'xxllncFilterAside'

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  content: `${PREFIX}-content`,
  toggle: `${PREFIX}-toggle`,
}

// Open/closed is remembered per browser.
const STORAGE_KEY = 'certificaten_filter_aside_open'

interface FilterAsideProps {
  children: ReactNode
}

const readOpen = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

/** Collapsible filter panel left of a list (react-admin `aside`), like xxllnc.ai. */
export const FilterAside: FC<FilterAsideProps> = ({ children }) => {
  // Open by default: filtering on target system is the main way to scan the list.
  const [open, setOpen] = useState(() => readOpen())
  const translate = useTranslate()
  // Collapsed, the badge shows that filters still apply.
  const activeCount = Object.values(useListContext().filterValues ?? {})
    .filter(value => (Array.isArray(value) ? value.length > 0 : value != null && value !== '')).length

  const toggle = (value: boolean) => {
    setOpen(value)
    try {
      localStorage.setItem(STORAGE_KEY, String(value))
    } catch {
      // Storage blocked: the panel just doesn't remember its state.
    }
  }

  return (
    <StyledRoot className={classes.root}>
      {!open && (
        <IconButton
          className={classes.toggle}
          onClick={() => toggle(true)}
          size="small"
          title={translate('filters.show')}
        >
          <Badge badgeContent={activeCount} color="primary">
            <FilterListIcon fontSize="small" />
          </Badge>
        </IconButton>
      )}
      <Collapse in={open} orientation="horizontal" unmountOnExit>
        <Card className={classes.content} elevation={0}>
          <Box className={classes.header}>
            <FilterListIcon sx={{ fontSize: 16, color: themeColor.slate40 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: themeColor.slate60, letterSpacing: '0.02em' }}>
              {translate('filters.title')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              onClick={() => toggle(false)}
              aria-label={translate('filters.hide')}
              sx={{ color: themeColor.slate40 }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          {children}
        </Card>
      </Collapse>
    </StyledRoot>
  )
}

const StyledRoot = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    order: -1,
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  [`& .${classes.toggle}`]: {
    ...cardSurfaceSx,
    padding: theme.spacing(1),
    marginRight: theme.spacing(2),
    color: themeColor.slate40,
    ['&:hover']: {
      backgroundColor: themeColor.slate05,
      color: themeColor.slate80,
    },
  },
  [`& .${classes.content}`]: {
    width: 220,
    marginRight: theme.spacing(2),
    ...cardSurfaceSx,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: border.thin + ' solid ' + border.light,
  },
}))
